import type {
  MarginModifierValue,
  PaddingModifierValue,
} from "schemas/generated/fileModifiers";

import React, { useCallback, useMemo, useState } from "react";

import DirectionBottom from "@svg/modifiers/direction/direction-bottom";
import DirectionHorizontal from "@svg/modifiers/direction/direction-horizontal";
import DirectionLeft from "@svg/modifiers/direction/direction-left";
import DirectionRight from "@svg/modifiers/direction/direction-right";
import DirectionTop from "@svg/modifiers/direction/direction-top";
import DirectionVertical from "@svg/modifiers/direction/direction-vertical";
import ExpandModifierOptions from "@svg/modifiers/expand-modifier-options";

import { Button } from "@replo/design-system/components/button/Button";
import { Tooltip } from "@replo/design-system/components/tooltip/Tooltip";
import { LengthInputSelector } from "@v2/features/modifiers/components/LengthInputSelector";
import { useOptimisticModifierValue } from "@v2/features/modifiers/hooks/useOptimisticModifierValue";
import { ChevronDown } from "lucide-react";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import {
  parseCommaSeparatedValues,
  parseUnit,
} from "replo-runtime/shared/utils/units";

import {
  MARGIN_MODIFIER_OPTIONS,
  PADDING_MODIFIER_OPTIONS,
} from "../constants";

type SpacingMode = "unified" | "individual";
type SpacingType = "margin" | "padding";
type SpacingModifier = MarginModifierValue | PaddingModifierValue;
type MarginValue = number | "auto";

interface SpacingValue {
  top?: MarginValue;
  right?: MarginValue;
  bottom?: MarginValue;
  left?: MarginValue;
}

interface SpacingField {
  key: string;
  label: string;
  value: MarginValue | undefined;
  /**
   * NOTE (Andrew, 2026-01-09, REPL-20714): Secondary value for unified mode - when this differs from value, shows comma-separated display
   * */
  secondaryValue?: MarginValue | undefined;
  onChange: (newValue: MarginValue | undefined) => void;
  /**
   * NOTE (Andrew, 2026-01-09, REPL-20714): Called when comma-separated values are changed independently (e.g., "10, 16" from arrow keys)
   * */
  onChangeMixed?: (
    primaryValue: MarginValue | undefined,
    secondaryValue: MarginValue | undefined,
  ) => void;
  icon?: React.ReactNode;
}

interface SpacingModifierConfig {
  type: SpacingType;
  unifiedTooltipText: string;
  individualTooltipText: string;
}

interface SpacingModifierProps<SpacingType extends SpacingModifier> {
  onChange: (modifiers: SpacingType[]) => void;
  config: SpacingModifierConfig;
}

/**
 * Common props for all LengthInputSelector instances
 */
function getCommonLengthInputProps(spacingType: SpacingType) {
  const options =
    spacingType === "margin"
      ? MARGIN_MODIFIER_OPTIONS
      : PADDING_MODIFIER_OPTIONS;

  // NOTE (Kevin, 2025-12-10): Only margin supports "auto" value in CSS
  const allowedNonUnitValues = spacingType === "margin" ? ["auto"] : [];

  // NOTE (Kevin, 2025-12-11): Padding cannot be negative, but margin can (for overlapping elements)
  const minValues = spacingType === "padding" ? { px: 0 } : undefined;

  return {
    placeholder: "0px",
    metrics: ["px"],
    unitDefaults: { value: "0", unit: "px" },
    menuOptions: options.map((option) => ({
      ...option,
      value: option.value.toString(),
    })),
    minValues,
    inputClassName: "w-full",
    endEnhancer: () => <ChevronDown size={12} />,
    allowedNonUnitValues,
  };
}

/**
 * Reusable SpacingInput component
 */
interface SpacingInputProps {
  field: SpacingField;
  spacingType: SpacingType;
}

/**
 * NOTE (Andrew, 2026-01-09, REPL-20714): Clamp a numeric value to 0 if negative and spacingType is padding.
 */
function clampForPadding(value: number, spacingType: SpacingType): number {
  return spacingType === "padding" && value < 0 ? 0 : value;
}

/**
 * NOTE (Andrew, 2026-01-09, REPL-20714): Parse a single value string to a MarginValue
 */
function parseSpacingValue(
  newValue: string,
  spacingType: SpacingType,
): MarginValue | undefined {
  const allowedNonUnitValues = spacingType === "margin" ? ["auto"] : [];
  const result = parseUnit(
    newValue,
    { value: 0, unit: "px" },
    "default",
    "px",
    undefined,
    undefined,
    allowedNonUnitValues,
  );

  if (result.value === "auto") {
    return "auto";
  }

  const numericValue =
    typeof result.value === "number"
      ? result.value
      : Number.parseFloat(result.value);
  return clampForPadding(numericValue, spacingType);
}

/**
 * NOTE (Andrew, 2026-01-09, REPL-20714): Format a single margin value for display.
 * When includeUnit is true, returns "10px"; when false, returns "10".
 * Returns fallback (default "") when value is nullish.
 */
function formatMarginValue(
  value: MarginValue | undefined,
  options: { includeUnit?: boolean; fallback?: string } = {},
): string {
  const { includeUnit = true, fallback = "" } = options;
  if (value === "auto") {
    return "Auto";
  }
  if (value != null) {
    return includeUnit ? `${value}px` : `${value}`;
  }
  return fallback;
}

const SpacingInput: React.FC<SpacingInputProps> = ({ field, spacingType }) => {
  const primaryValue = field.value;
  const secondaryValue = field.secondaryValue;
  const hasMixedValues =
    secondaryValue !== undefined && primaryValue !== secondaryValue;

  const displayValue = hasMixedValues
    ? `${formatMarginValue(primaryValue, { fallback: "0px" })}, ${formatMarginValue(secondaryValue, { fallback: "0px" })}`
    : formatMarginValue(primaryValue);

  const handleValueChange = useCallback(
    (newValue: string) => {
      const parsed = parseCommaSeparatedValues(newValue);
      if (parsed && field.onChangeMixed) {
        field.onChangeMixed(
          clampForPadding(parsed.first, spacingType),
          clampForPadding(parsed.second, spacingType),
        );
        return;
      }
      field.onChange(parseSpacingValue(newValue, spacingType));
    },
    [field, spacingType],
  );

  const baseAllowedNonUnitValues =
    spacingType === "margin" ? ["auto", "Auto"] : [];
  const allowedNonUnitValues = hasMixedValues
    ? [...baseAllowedNonUnitValues, displayValue]
    : baseAllowedNonUnitValues;

  return (
    <LengthInputSelector
      field={field.key}
      value={displayValue}
      onChange={handleValueChange}
      startEnhancer={field.icon}
      {...getCommonLengthInputProps(spacingType)}
      allowedNonUnitValues={allowedNonUnitValues}
    />
  );
};

/**
 * Determine initial mode based on spacing consistency.
 * Returns "individual" if any spacing values differ (user likely set them individually).
 * Returns "unified" only if all values are consistent (suitable for unified inputs).
 */
function getInitialMode(spacingValue: SpacingValue): SpacingMode {
  const verticalEqual = spacingValue.top === spacingValue.bottom;
  const horizontalEqual = spacingValue.left === spacingValue.right;

  // Only use unified mode if both vertical AND horizontal pairs are equal
  // If any values differ, start in individual mode to preserve user's intent
  return verticalEqual && horizontalEqual ? "unified" : "individual";
}

const SpacingModifier = <SpacingType extends SpacingModifier>({
  onChange,
  config,
}: SpacingModifierProps<SpacingType>) => {
  // Get optimistic values for each direction separately
  const topValue = useOptimisticModifierValue({
    modifierType: { type: config.type, direction: "top", value: 0 },
    extractor: (elementDesignation) => {
      const stylePrefix = config.type === "margin" ? "margin" : "padding";
      return (
        elementDesignation.element.styleOverrides?.[`${stylePrefix}Top`] ??
        undefined
      );
    },
  });

  const rightValue = useOptimisticModifierValue({
    modifierType: { type: config.type, direction: "right", value: 0 },
    extractor: (elementDesignation) => {
      const stylePrefix = config.type === "margin" ? "margin" : "padding";
      return (
        elementDesignation.element.styleOverrides?.[`${stylePrefix}Right`] ??
        undefined
      );
    },
  });

  const bottomValue = useOptimisticModifierValue({
    modifierType: { type: config.type, direction: "bottom", value: 0 },
    extractor: (elementDesignation) => {
      const stylePrefix = config.type === "margin" ? "margin" : "padding";
      return (
        elementDesignation.element.styleOverrides?.[`${stylePrefix}Bottom`] ??
        undefined
      );
    },
  });

  const leftValue = useOptimisticModifierValue({
    modifierType: { type: config.type, direction: "left", value: 0 },
    extractor: (elementDesignation) => {
      const stylePrefix = config.type === "margin" ? "margin" : "padding";
      return (
        elementDesignation.element.styleOverrides?.[`${stylePrefix}Left`] ??
        undefined
      );
    },
  });

  const extractedValue = useMemo(() => {
    return {
      top: topValue,
      right: rightValue,
      bottom: bottomValue,
      left: leftValue,
    };
  }, [topValue, rightValue, bottomValue, leftValue]);

  const [currentValue, setCurrentValue] = useOverridableState(
    extractedValue,
    undefined, // NOTE (Kevin, 2025-07-28): We handle onChange in updateSpacingValue
  );

  const [mode, setMode] = useState<SpacingMode>(() => {
    return getInitialMode(currentValue);
  });

  const createModifiersValues = useCallback(
    (value: SpacingValue) => {
      const modifiers: SpacingType[] = [];

      if (value.top !== undefined) {
        modifiers.push({
          type: config.type,
          direction: "top",
          value: value.top,
        } as SpacingType);
      }
      if (value.right !== undefined) {
        modifiers.push({
          type: config.type,
          direction: "right",
          value: value.right,
        } as SpacingType);
      }
      if (value.bottom !== undefined) {
        modifiers.push({
          type: config.type,
          direction: "bottom",
          value: value.bottom,
        } as SpacingType);
      }
      if (value.left !== undefined) {
        modifiers.push({
          type: config.type,
          direction: "left",
          value: value.left,
        } as SpacingType);
      }

      return modifiers;
    },
    [config.type],
  );

  const updateSpacingValue = useCallback(
    (updates: Partial<SpacingValue>) => {
      const newValue = { ...currentValue, ...updates };
      setCurrentValue(newValue);
      const modifiers = createModifiersValues(newValue);
      // TODO (Kurt, 2025-01-27): Bundle up the value instead of sending individual modifier operations
      onChange(modifiers);
    },
    [currentValue, setCurrentValue, createModifiersValues, onChange],
  );

  const inputConfigs: Record<SpacingMode, SpacingField[]> = useMemo(
    () => ({
      unified: [
        {
          key: "vertical",
          label: "Vertical",
          icon: <DirectionVertical className="w-4 h-4" />,
          value: currentValue?.top,
          secondaryValue: currentValue?.bottom,
          onChange: (newValue) => {
            updateSpacingValue({
              top: newValue,
              bottom: newValue,
            });
          },
          onChangeMixed: (topValue, bottomValue) => {
            updateSpacingValue({
              top: topValue,
              bottom: bottomValue,
            });
          },
        },
        {
          key: "horizontal",
          label: "Horizontal",
          icon: <DirectionHorizontal className="w-4 h-4" />,
          value: currentValue?.left,
          secondaryValue: currentValue?.right,
          onChange: (newValue) => {
            updateSpacingValue({
              left: newValue,
              right: newValue,
            });
          },
          onChangeMixed: (leftValue, rightValue) => {
            updateSpacingValue({
              left: leftValue,
              right: rightValue,
            });
          },
        },
      ],
      individual: [
        {
          key: "top",
          label: "Top",
          icon: <DirectionTop className="w-4 h-4" />,
          value: currentValue?.top,
          onChange: (newValue) => {
            updateSpacingValue({ top: newValue });
          },
        },
        {
          key: "right",
          label: "Right",
          icon: <DirectionRight className="w-4 h-4" />,
          value: currentValue?.right,
          onChange: (newValue) => {
            updateSpacingValue({ right: newValue });
          },
        },
        {
          key: "bottom",
          label: "Bottom",
          icon: <DirectionBottom className="w-4 h-4" />,
          value: currentValue?.bottom,
          onChange: (newValue) => {
            updateSpacingValue({ bottom: newValue });
          },
        },
        {
          key: "left",
          label: "Left",
          icon: <DirectionLeft className="w-4 h-4" />,
          value: currentValue?.left,
          onChange: (newValue) => {
            updateSpacingValue({ left: newValue });
          },
        },
      ],
    }),
    [currentValue, updateSpacingValue],
  );

  const inputs = inputConfigs[mode];

  const handleModeToggle = () => {
    setMode(mode === "unified" ? "individual" : "unified");
  };

  return (
    <>
      {mode === "unified" ? (
        <div className="grid grid-cols-[1fr_1fr_min-content] gap-2 items-center">
          {inputs.map((input) => (
            <SpacingInput
              key={input.key}
              field={input}
              spacingType={config.type}
            />
          ))}
          <Tooltip content={config.individualTooltipText}>
            <Button
              icon
              variant="secondary"
              size="sm"
              onClick={handleModeToggle}
            >
              <ExpandModifierOptions />
            </Button>
          </Tooltip>
        </div>
      ) : (
        <div className="grid grid-cols-[1fr_1fr_min-content] gap-2 items-center">
          {/* First row */}
          {inputs.slice(0, 2).map((input) => (
            <SpacingInput
              key={input.key}
              field={input}
              spacingType={config.type}
            />
          ))}
          <Tooltip content={config.unifiedTooltipText}>
            <Button
              icon
              variant="secondary"
              size="sm"
              onClick={handleModeToggle}
            >
              <ExpandModifierOptions />
            </Button>
          </Tooltip>
          {/* Second row */}
          {inputs.slice(2).map((input) => (
            <SpacingInput
              key={input.key}
              field={input}
              spacingType={config.type}
            />
          ))}
          <div></div> {/* Empty space to align with button column */}
        </div>
      )}
    </>
  );
};

export default SpacingModifier;
